import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { RefreshCw, ShieldAlert } from "lucide-react"
import { useCallback, useMemo } from "react"
import { z } from "zod"

import { Button } from "@/components/button"
import { EmptyState } from "@/components/shared/empty-state"
import { StatusAlert } from "@/components/shared/status-alert"
import { BossDetailPanel } from "@/components/talent-overview/boss-detail-panel"
import { BossFocusSidebar } from "@/components/talent-overview/boss-focus-sidebar"
import {
  CATEGORY_ORDER,
  SEASONS,
  getMemberDisplayName,
} from "@/components/talent-overview/constants"
import { TalentOverviewIntro } from "@/components/talent-overview/talent-overview-intro"
import { DashboardSkeleton } from "@/components/ui/page-skeletons"
import { BOSS_SEASONS } from "@/core/talent-tracking/seasons"
import { talentTrackingKeys } from "@/lib/query-keys"
import {
  createTalentOverviewUiStore,
  useTalentOverviewRouteStore,
} from "@/lib/stores/talent-overview-ui-store"
import {
  talentTrackingApi,
  type MissingTalentSummary,
  type TalentTrackingCategory,
} from "@/lib/talent-tracking-api"

const CATEGORY_VALUES = ["offensive", "defensive", "strategic"] as const
const CATEGORY_FILTER_VALUES = ["all", ...CATEGORY_VALUES] as const

type BossSortMode = "most-missing" | "least-missing" | "name"
type TalentSortMode = "most-missing" | "tier" | "name"
type OverviewMode = "cards" | "matrix"

const BOSS_SORT_MODES = ["most-missing", "least-missing", "name"] as const
const TALENT_SORT_MODES = ["most-missing", "tier", "name"] as const
const OVERVIEW_MODES = ["cards", "matrix"] as const
const CARD_SORT_MODES = ["severity", "tier"] as const

const optionalTrimmedStringSchema = z
  .preprocess((value) => {
    if (typeof value !== "string") {
      return undefined
    }

    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }, z.string())
  .optional()

const optionalBooleanSchema = z
  .preprocess((value) => {
    if (typeof value === "boolean") {
      return value
    }

    if (typeof value !== "string") {
      return undefined
    }

    const normalized = value.trim().toLowerCase()
    if (normalized === "true" || normalized === "1") {
      return true
    }

    if (normalized === "false" || normalized === "0") {
      return false
    }

    return undefined
  }, z.boolean())
  .optional()

const optionalEnumSchema = <TValues extends readonly [string, ...string[]]>(
  values: TValues,
) =>
  z
    .preprocess(
      (value) => (typeof value === "string" ? value : undefined),
      z.enum(values),
    )
    .optional()

const talentOverviewSearchSchema = z.object({
  season: optionalEnumSchema(SEASONS),
  bossId: optionalTrimmedStringSchema,
  category: optionalEnumSchema(CATEGORY_FILTER_VALUES),
  onlyMissing: optionalBooleanSchema,
  mode: optionalEnumSchema(OVERVIEW_MODES),
  bossSort: optionalEnumSchema(BOSS_SORT_MODES),
  talentSort: optionalEnumSchema(TALENT_SORT_MODES),
  cardSort: optionalEnumSchema(CARD_SORT_MODES),
  memberQuery: optionalTrimmedStringSchema,
  advancedFilters: optionalBooleanSchema,
})

type TalentOverviewSearch = z.infer<typeof talentOverviewSearchSchema>

const DEFAULT_TALENT_OVERVIEW_SEARCH: Required<
  Omit<TalentOverviewSearch, "bossId" | "memberQuery">
> = {
  season: BOSS_SEASONS[0],
  category: "all",
  onlyMissing: true,
  mode: "cards",
  bossSort: "most-missing",
  talentSort: "most-missing",
  cardSort: "severity",
  advancedFilters: false,
}

export const Route = createFileRoute("/_authed/_admin/talent-overview")({
  validateSearch: talentOverviewSearchSchema,
  beforeLoad: () => ({
    talentOverviewStore: createTalentOverviewUiStore(),
  }),
  component: TalentOverviewRoute,
})

const compactTalentOverviewSearch = (
  search: TalentOverviewSearch,
): TalentOverviewSearch => {
  const compacted: TalentOverviewSearch = {}

  if (
    search.season &&
    search.season !== DEFAULT_TALENT_OVERVIEW_SEARCH.season
  ) {
    compacted.season = search.season
  }

  if (search.bossId) {
    compacted.bossId = search.bossId
  }

  if (
    search.category &&
    search.category !== DEFAULT_TALENT_OVERVIEW_SEARCH.category
  ) {
    compacted.category = search.category
  }

  if (
    search.onlyMissing !== undefined &&
    search.onlyMissing !== DEFAULT_TALENT_OVERVIEW_SEARCH.onlyMissing
  ) {
    compacted.onlyMissing = search.onlyMissing
  }

  if (search.mode && search.mode !== DEFAULT_TALENT_OVERVIEW_SEARCH.mode) {
    compacted.mode = search.mode
  }

  if (
    search.bossSort &&
    search.bossSort !== DEFAULT_TALENT_OVERVIEW_SEARCH.bossSort
  ) {
    compacted.bossSort = search.bossSort
  }

  if (
    search.talentSort &&
    search.talentSort !== DEFAULT_TALENT_OVERVIEW_SEARCH.talentSort
  ) {
    compacted.talentSort = search.talentSort
  }

  if (
    search.cardSort &&
    search.cardSort !== DEFAULT_TALENT_OVERVIEW_SEARCH.cardSort
  ) {
    compacted.cardSort = search.cardSort
  }

  if (
    search.advancedFilters !== undefined &&
    search.advancedFilters !== DEFAULT_TALENT_OVERVIEW_SEARCH.advancedFilters
  ) {
    compacted.advancedFilters = search.advancedFilters
  }

  const effectiveMode = search.mode ?? DEFAULT_TALENT_OVERVIEW_SEARCH.mode
  const trimmedMemberQuery = search.memberQuery?.trim()
  if (effectiveMode === "matrix" && trimmedMemberQuery) {
    compacted.memberQuery = trimmedMemberQuery
  }

  return compacted
}

const compareTalents = (
  left: MissingTalentSummary,
  right: MissingTalentSummary,
  mode: TalentSortMode,
  groupByCategory: boolean,
): number => {
  if (groupByCategory) {
    const categoryOrder =
      CATEGORY_ORDER[left.category] - CATEGORY_ORDER[right.category]
    if (categoryOrder !== 0) {
      return categoryOrder
    }
  }

  if (mode === "most-missing") {
    if (right.missingCount !== left.missingCount) {
      return right.missingCount - left.missingCount
    }
  } else if (mode === "tier") {
    if (left.tier !== right.tier) {
      return left.tier - right.tier
    }
  } else if (mode === "name") {
    const descriptionOrder = left.description.localeCompare(right.description)
    if (descriptionOrder !== 0) {
      return descriptionOrder
    }
  }

  if (left.tier !== right.tier) {
    return left.tier - right.tier
  }

  const categoryOrder =
    CATEGORY_ORDER[left.category] - CATEGORY_ORDER[right.category]
  if (categoryOrder !== 0) {
    return categoryOrder
  }

  return left.description.localeCompare(right.description)
}

function TalentOverviewRoute() {
  const store = useTalentOverviewRouteStore()
  const search = Route.useSearch()
  const selectedSeason = search.season ?? DEFAULT_TALENT_OVERVIEW_SEARCH.season
  const selectedBossId = search.bossId
  const selectedCategory =
    search.category ?? DEFAULT_TALENT_OVERVIEW_SEARCH.category
  const showOnlyMissing =
    search.onlyMissing ?? DEFAULT_TALENT_OVERVIEW_SEARCH.onlyMissing
  const overviewMode = search.mode ?? DEFAULT_TALENT_OVERVIEW_SEARCH.mode
  const bossSortMode =
    search.bossSort ?? DEFAULT_TALENT_OVERVIEW_SEARCH.bossSort
  const talentSortMode =
    search.talentSort ?? DEFAULT_TALENT_OVERVIEW_SEARCH.talentSort
  const cardSortMode =
    search.cardSort ?? DEFAULT_TALENT_OVERVIEW_SEARCH.cardSort
  const memberQuery = search.memberQuery ?? ""
  const isAdvancedFiltersOpen =
    search.advancedFilters ?? DEFAULT_TALENT_OVERVIEW_SEARCH.advancedFilters
  const navigate = Route.useNavigate()

  const overviewQuery = useQuery({
    queryKey: talentTrackingKeys.overview(),
    queryFn: () => talentTrackingApi.getOverview(),
  })

  const updateSearch = useCallback(
    (
      updates: Record<string, string | boolean | undefined>,
      options?: { replace?: boolean },
    ) => {
      void navigate({
        search: (prev) => {
          const next = compactTalentOverviewSearch({
            ...(prev as TalentOverviewSearch),
            ...updates,
          })

          return {
            season: next.season,
            bossId: next.bossId,
            category: next.category,
            onlyMissing: next.onlyMissing,
            mode: next.mode,
            bossSort: next.bossSort,
            talentSort: next.talentSort,
            cardSort: next.cardSort,
            memberQuery: next.memberQuery,
            advancedFilters: next.advancedFilters,
          }
        },
        replace: options?.replace ?? true,
        resetScroll: false,
      })
    },
    [navigate],
  )

  const seasonBosses = useMemo(() => {
    const bosses =
      overviewQuery.data?.bosses.filter(
        (boss) => boss.season === selectedSeason,
      ) ?? []

    return [...bosses].sort((left, right) => {
      if (bossSortMode === "most-missing") {
        if (right.missingCount !== left.missingCount) {
          return right.missingCount - left.missingCount
        }
      }

      if (bossSortMode === "least-missing") {
        if (left.missingCount !== right.missingCount) {
          return left.missingCount - right.missingCount
        }
      }

      return left.name.localeCompare(right.name)
    })
  }, [bossSortMode, overviewQuery.data, selectedSeason])

  const selectedBoss = useMemo(() => {
    if (!selectedBossId) {
      return seasonBosses[0] ?? null
    }

    return (
      seasonBosses.find((boss) => boss.id === selectedBossId) ??
      seasonBosses[0] ??
      null
    )
  }, [seasonBosses, selectedBossId])

  const metrics = {
    members: overviewQuery.data?.members.length ?? 0,
    bosses: seasonBosses.length,
    missing: seasonBosses.reduce((total, boss) => total + boss.missingCount, 0),
  }

  const visibleTalents = useMemo(() => {
    if (!selectedBoss) {
      return []
    }

    const groupByCategory =
      selectedCategory === "all" && overviewMode === "matrix"

    return selectedBoss.talents
      .filter((talent) => {
        if (
          selectedCategory !== "all" &&
          talent.category !== selectedCategory
        ) {
          return false
        }

        if (showOnlyMissing && talent.missingCount === 0) {
          return false
        }

        return true
      })
      .sort((left, right) =>
        compareTalents(left, right, talentSortMode, groupByCategory),
      )
  }, [
    overviewMode,
    selectedBoss,
    selectedCategory,
    showOnlyMissing,
    talentSortMode,
  ])

  const matrixRows = useMemo(() => {
    const members = overviewQuery.data?.members ?? []
    const normalizedMemberQuery =
      overviewMode === "matrix" ? memberQuery.trim().toLowerCase() : ""

    return [...members]
      .map((member) => {
        const missingCount = visibleTalents.reduce(
          (total, talent) =>
            total + (talent.missingUserIds.includes(member.userId) ? 1 : 0),
          0,
        )

        return {
          userId: member.userId,
          displayName: member.displayName,
          label: getMemberDisplayName(member),
          missingCount,
          completedCount: visibleTalents.length - missingCount,
        }
      })
      .filter((member) => {
        if (showOnlyMissing && member.missingCount === 0) {
          return false
        }

        if (
          normalizedMemberQuery &&
          !member.label.toLowerCase().includes(normalizedMemberQuery)
        ) {
          return false
        }

        return true
      })
      .sort((left, right) => {
        if (right.missingCount !== left.missingCount) {
          return right.missingCount - left.missingCount
        }

        return left.label.localeCompare(right.label)
      })
  }, [
    memberQuery,
    overviewMode,
    overviewQuery.data?.members,
    showOnlyMissing,
    visibleTalents,
  ])

  const matrixTalents = useMemo(() => visibleTalents, [visibleTalents])

  if (overviewQuery.isLoading) {
    return <DashboardSkeleton />
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <div className="grid gap-4">
        <StatusAlert tone="danger">
          {overviewQuery.error instanceof Error
            ? overviewQuery.error.message
            : "Failed to load talent readiness overview"}
        </StatusAlert>
        <Button
          type="button"
          variant="outline"
          onClick={() => void overviewQuery.refetch()}
        >
          <RefreshCw className="size-4" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      <TalentOverviewIntro
        store={store}
        metrics={metrics}
        selectedSeason={selectedSeason}
        selectedBoss={selectedBoss}
        visibleTalents={visibleTalents}
        isFetching={overviewQuery.isFetching}
        onRefresh={() => void overviewQuery.refetch()}
      />

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <BossFocusSidebar
          seasonBosses={seasonBosses}
          selectedSeason={selectedSeason}
          selectedBoss={selectedBoss}
          bossSortMode={bossSortMode}
          onUpdateSearch={updateSearch}
        />

        <div className="grid gap-4">
          {selectedBoss ? (
            <BossDetailPanel
              store={store}
              selectedBoss={selectedBoss}
              overviewMode={overviewMode}
              showOnlyMissing={showOnlyMissing}
              selectedCategory={selectedCategory}
              talentSortMode={talentSortMode}
              cardSortMode={cardSortMode}
              isAdvancedFiltersOpen={isAdvancedFiltersOpen}
              visibleTalents={visibleTalents}
              matrixRows={matrixRows}
              matrixTalents={matrixTalents}
              memberQuery={memberQuery}
              totalMembers={overviewQuery.data.members.length}
              onUpdateSearch={updateSearch}
            />
          ) : (
            <EmptyState
              icon={<ShieldAlert className="size-5 text-muted-foreground" />}
              title="No bosses configured"
              description={`No bosses are configured for ${selectedSeason} yet. Add bosses in the talent config editor.`}
            />
          )}
        </div>
      </div>
    </div>
  )
}
